import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabaseClient';

const PAGE_SIZE = 50;

const UserList = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const [nameFilter, setNameFilter] = useState('');
    const [idFilter, setIdFilter] = useState('');
    const [joinDateFilter, setJoinDateFilter] = useState('');
    const [currentFilters, setCurrentFilters] = useState({});
    const observerTarget = useRef(null);

    const buildQuery = (filters = {}) => {
        let query = supabase.from('users').select('*', { count: 'exact' });

        if (filters.name) {
            query = query.ilike('name', `%${filters.name}%`);
        }

        if (filters.userId) {
            query = query.ilike('email', `%${filters.userId}%`);
        }

        if (filters.joinDate) {
            const start = `${filters.joinDate}T00:00:00.000Z`;
            const end = `${filters.joinDate}T23:59:59.999Z`;
            query = query.gte('created_at', start).lte('created_at', end);
        }

        return query.order('created_at', { ascending: false });
    };

    const fetchUsers = async (filters = {}) => {
        setLoading(true);
        setOffset(0);
        setHasMore(true);
        setUsers([]);
        setCurrentFilters(filters);

        try {
            const query = buildQuery(filters);
            const { data, error } = await query.range(0, PAGE_SIZE - 1);

            if (error) {
                console.error('Error fetching users:', error.message);
                throw error;
            }
            setUsers(data || []);
            setOffset(PAGE_SIZE);
            setHasMore((data?.length || 0) >= PAGE_SIZE);
        } catch (error) {
            console.error('Error fetching users:', error.message);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const loadMore = async () => {
        if (loadingMore || !hasMore) return;

        setLoadingMore(true);
        try {
            const query = buildQuery(currentFilters);
            const { data, error } = await query.range(offset, offset + PAGE_SIZE - 1);

            if (error) {
                console.error('Error loading more users:', error.message);
                throw error;
            }
            const newData = data || [];
            setUsers(prev => [...prev, ...newData]);
            setOffset(prev => prev + PAGE_SIZE);
            setHasMore(newData.length >= PAGE_SIZE);
        } catch (error) {
            console.error('Error loading more users:', error.message);
        } finally {
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    // Intersection Observer for infinite scroll
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
                    loadMore();
                }
            },
            { threshold: 0.1 }
        );

        if (observerTarget.current) {
            observer.observe(observerTarget.current);
        }

        return () => {
            if (observerTarget.current) {
                observer.unobserve(observerTarget.current);
            }
        };
    }, [hasMore, loadingMore, loading, offset, currentFilters]);

    const handleSearch = (event) => {
        event.preventDefault();
        fetchUsers({
            name: nameFilter.trim(),
            userId: idFilter.trim(),
            joinDate: joinDateFilter,
        });
    };

    const handleReset = () => {
        setNameFilter('');
        setIdFilter('');
        setJoinDateFilter('');
        fetchUsers();
    };

    if (loading) {
        return (
            <div className="card shadow mb-4">
                <div className="card-header py-3">
                    <h6 className="m-0 font-weight-bold text-primary">사용자 목록</h6>
                </div>
                <div className="card-body">
                    <p>Loading users...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="card shadow mb-4">
            <div className="card-header py-3">
                <h6 className="m-0 font-weight-bold text-primary">사용자 목록</h6>
            </div>
            <div className="card-body">
                <form className="mb-4 p-3 bg-light rounded border" onSubmit={handleSearch}>
                    <div className="form-row align-items-end">
                        <div className="form-group col-md-3 mb-2 mb-md-0">
                            <label className="small font-weight-bold mb-1">이름</label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="이름 검색"
                                value={nameFilter}
                                onChange={(event) => setNameFilter(event.target.value)}
                            />
                        </div>
                        <div className="form-group col-md-3 mb-2 mb-md-0">
                            <label className="small font-weight-bold mb-1">아이디(이메일)</label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="아이디 검색"
                                value={idFilter}
                                onChange={(event) => setIdFilter(event.target.value)}
                            />
                        </div>
                        <div className="form-group col-md-3 mb-2 mb-md-0">
                            <label className="small font-weight-bold mb-1">가입일</label>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={joinDateFilter}
                                onChange={(event) => setJoinDateFilter(event.target.value)}
                            />
                        </div>
                        <div className="form-group col-md-3 mb-0 text-md-right">
                            <button type="submit" className="btn btn-primary btn-sm mr-2" disabled={loading}>
                                조회
                            </button>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={handleReset} disabled={loading}>
                                초기화
                            </button>
                        </div>
                    </div>
                </form>
                <div className="table-responsive">
                    <table className="table table-bordered" id="dataTable" width="100%" cellSpacing="0">
                        <thead>
                            <tr>
                                <th>이름</th>
                                <th>이메일</th>
                                <th>등급</th>
                                <th>가입일</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.length > 0 ? (
                                users.map(user => (
                                    <tr key={user.id}>
                                        <td>{user.name || 'N/A'}</td>
                                        <td>{user.email || 'N/A'}</td>
                                        <td>{user.grade || 'N/A'}</td>
                                        <td>{user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="text-center">사용자 데이터가 없습니다.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* 스크롤 페이징 트리거 */}
                <div ref={observerTarget} className="text-center py-4">
                    {loadingMore && (
                        <div className="text-muted">
                            <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                            더 불러오는 중...
                        </div>
                    )}
                    {!hasMore && users.length > 0 && (
                        <small className="text-muted">모든 데이터를 불러왔습니다.</small>
                    )}
                </div>

                <div className="mt-3">
                    <p><strong>총 사용자 수:</strong> {users.length}명</p>
                </div>
            </div>
        </div>
    );
};



export default UserList;